import { takeLatest, call, put, select } from "redux-saga/effects";
import { isAdminLocal, languageOptions, orderByFilterTable, Roles } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./users-api";
import { IRequestUpdateUser } from "./users-api";
import { Types, Creators } from "./users-duck";

export const getFilterFromState = (state) => state.Users;
export const getLocationFromState = (state) => state.Location;
export const getFilterFromStateUserSession = (state) => state.UserSession;

export const onExtractRegisterData = (registersData, roles) =>
  registersData.items.map((item) => {
    return {
      id: item.id,
      active: item.active,
      name: item.name,
      email: item.email,
      jobTitle: item.jobTitle,
      role: item.role,
      locationDescription: item.locationDescription,
      disabledEdit: roles !== Roles.ADMIN,
    };
  });

export const onExtractUserData = (registersData, roles, locations, languageOptions) => {
  return {
    id: registersData.id,
    active: registersData.active,
    name: registersData.name,
    email: registersData.email,
    jobTitle: registersData.jobTitle,
    role: roles.filter((x) => x.id === registersData.roleId && x.description)[0].description,
    locationDescription: locations.filter((x) => x.id === registersData.locationId && x.description)[0].description,
    allowMultipleLocations: registersData.allowMultipleLocations,
    locations: registersData.locations.filter((item) => item.id !== registersData.locationId),
    languageDescription: languageOptions.filter((x) => x.description === registersData.language && x.description)[0].description,
  };
};

const onUpdateUserData = (item: IRequestUpdateUser) => {
  // eslint-disable-next-line prefer-const
  let activesLocations = item.locations;
  activesLocations.push(item.location);
  return {
    allowMultipleLocations: item.allowMultipleLocations,
    id: item.id,
    jobTitle: item.jobTitle,
    location: item.location,
    locations: activesLocations,
    roleId: item.roleId,
    language: item.language,
  };
};

export function* onUsersFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!isAdminLocal(userSessionState.userData.role)) {
    return;
  }
  try {
    yield put(Creators.usersShowLoadingTable());
    const users = yield select(getFilterFromState);
    const isAscending = users.filter.ascend === "ascend" ? true : false;
    const orderByFilter = orderByFilterTable(users);
    const resp = yield call(Api.getUsers, {
      filter: users.searchFilter,
      orderBy: orderByFilter,
      ascending: isAscending,
      _size: users.filter.itemsByPage,
      _page: users.filter.pageIndex,
    });
    const registersData = resp.data;
    const data = onExtractRegisterData(registersData, userSessionState.userData.role);
    yield put(Creators.usersFetchRegister(data, registersData.totalItems));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_users");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.usersHideLoadingTable());
  }
}

export function* onUsersUpdateUserAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!isAdminLocal(userSessionState.userData.role)) {
    return;
  }
  try {
    yield put(Creators.usersShowLoading());
    const userData = onUpdateUserData(action.body);
    yield call(Api.updateUser, userData);
    const registrationSuccessfullyMessage = yield translate("update_successfully");
    yield call(YmsNotification, {
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(Creators.usersEditActive(false));
    yield put(Creators.usersUpdateFilter(""));
    yield onUsersFetchAsync();
  } catch (error) {
    const registrationErrorMessage = yield translate("cannot_register");
    yield call(YmsNotification, {
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.usersHideLoading());
  }
}

export function* onFetchRolesAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!isAdminLocal(userSessionState.userData.role)) {
    return;
  }
  try {
    const rolesResponse = yield call(Api.getRoles);
    const rolesList = rolesResponse.data;
    const roles = { rolesList };
    yield put(Creators.usersRolesList(roles));
  } catch (error) {
    const requestErrorMessage = yield translate("could_not_load_roles");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onUserFetchByIdAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!isAdminLocal(userSessionState.userData.role)) {
    return;
  }
  try {
    yield put(Creators.usersShowLoading());
    const users = yield select(getFilterFromState);
    const location = yield select(getLocationFromState);
    const resp = yield call(Api.getUserById, {
      id: action.id,
    });
    const userData = yield onExtractUserData(resp.data, users.rolesList, location.locationActiveList, languageOptions);
    yield put(Creators.usersUpdateUser(userData));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("user_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.usersHideLoading());
  }
}

export default function* UsersSagas() {
  yield takeLatest(Types.USERS_FETCH_ASYNC, onUsersFetchAsync);
  yield takeLatest(Types.USERS_UPDATE_USER_ASYNC, onUsersUpdateUserAsync);
  yield takeLatest(Types.USERS_ROLES_FETCH_ASYNC, onFetchRolesAsync);
  yield takeLatest(Types.USERS_FETCH_BY_ID_ASYNC, onUserFetchByIdAsync);
}
